import { ViewModelTypeDecorator } from '../../decorators/view-model-type.decorator';
import { UIStarter } from '../../starter/ui-starter';
import { CommandFactory } from '../commands/command-factory';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { MessageResourceManager } from '../../resources/message-resource-manager';
import { MessageCodes } from '../../resources/message-codes';
import { ModalService } from './modal.service';
import { LogService, Message } from '@nts/std/utility';
import { MessageButton, MessageResult, MessageType } from '@nts/std/types';
import type { ExternalModalViewModelInterface, IFrameGetter, StatusMessageInterface, TabSocketInterface, UIResultCommandInterface } from '@nts/std/interfaces';

@ViewModelTypeDecorator(ExternalModalViewModel)
export class ExternalModalViewModel implements ExternalModalViewModelInterface {

  modalTitle: string;
  modalSubtitle = '';
  showFooter = true;
  supportRemoteClosingCheck = false;
  supportRemoteStatus = false;
  baseUrl: string;
  jsonIdentity: string;
  additionalQueryParams = new URLSearchParams();
  externalReturn: boolean;
  socket: TabSocketInterface;
  onActionInProgress = new Subject<boolean>();
  modalCommands = [];
  cancelAction: Subject<void> = new Subject();
  selectCommand: UIResultCommandInterface<string, any>;

  closeModal = () => this.canICloseModal();

  // Real-time status tracking from iframe push events
  private statusPushSubject$ = new Subject<StatusMessageInterface>();
  private lastPushedStatus: StatusMessageInterface | null = null;

  private _message: string;
  get message(): string {
    return this._message;
  }
  set message(v: string) {
    this._message = v;
  }


  private _caption: string;
  get caption(): string {
    return this._caption;
  }
  set caption(v: string) {
    this._caption = v;
  }


  private _detail: string;
  get detail(): string {
    return this._detail;
  }
  set detail(v: string) {
    this._detail = v;
  }

  async initialize(args: IFrameGetter): Promise<void> {

    if (this.externalReturn) {
      this.socket = await UIStarter.startIframeClientAndReturn(
        this.baseUrl,
        (payload) => {
          switch (payload.type) {
            case MessageType.ExternalReturn:
              this.selectCommand.execute(payload.data);
              break;
            default:
              break;
          }
        },
        () => this.cancelAction.next(),
        this.jsonIdentity,
        this.additionalQueryParams,
        args
      );
    } else if (this.supportRemoteClosingCheck) {
      this.socket = await UIStarter.startIframeClientAndReturn(
        this.baseUrl,
        (payload) => {
          switch (payload.type) {
            case MessageType.ExternalReturn:
              this.selectCommand.execute(payload.data);
              break;
            default:
              break;
          }
        },
        () => this.cancelAction.next(),
        this.jsonIdentity,
        this.additionalQueryParams,
        args,
        false
      );
    } else {
      UIStarter.startIframeClientAndWithoutReturn(
        this.baseUrl,
        () => this.cancelAction.next(),
        this.jsonIdentity,
        this.additionalQueryParams,
        args
      );
    }
  }

  async canICloseModal(): Promise<boolean> {
    if (this.supportRemoteClosingCheck) {
      this.onActionInProgress.next(true);
      const response = await this.checkStatus();
      this.onActionInProgress.next(false);

      if (response.hasModalOpen) {
        // TODO fare vedere un messaggio che dice chiudere prima le modali figlie
        return false;
      }

      if (response.pendingChanges === true) {
        const result = await this.modalService.showMessageAsync(
          MessageResourceManager.Current.getMessage(MessageCodes.Warning),
          MessageResourceManager.Current.getMessage('Modal_CloseConfirmMessage'),
          MessageButton.YesNo);
        if (result === MessageResult.Yes) {
          this.socket.destroy();
        }
        return result === MessageResult.Yes;
      }
    }
    return true;
  }

  async checkStatus(): Promise<StatusMessageInterface> {
    if (this.supportRemoteStatus) {
      const response = await this.socket.sendWithConfirmAsync<string, StatusMessageInterface>(
        {
          message: 'check-status',
          messageType: MessageType.AskStatus
        }
      ).catch((err) => {
        LogService.log(err);
        return new Message(false);
      });
      if (response.data !== false) {
        const data: StatusMessageInterface = response.data as StatusMessageInterface;
        return {
          hasModalOpen: data.hasModalOpen,
          pendingChanges: data.pendingChanges,
          version: data.version
        };
      }
    }
    if (this.supportRemoteClosingCheck) {
      const response = await this.socket.sendWithConfirmAsync<string, boolean>(
        {
          message: 'check-pending-changes',
          messageType: MessageType.AskPendingChanges
        }
      ).catch((err) => {
        LogService.log(err);
        return new Message(false);
      });

      return {
        hasModalOpen: false,
        pendingChanges: response.data === true,
        version: 0
      };
    }
    return {
      hasModalOpen: false,
      pendingChanges: false,
      version: 0
    };
  }

  /**
   * Handle real-time status push events from iframe component
   * Called by ExternalModalContainerComponent when statusChanged is emitted
   * @param status The status data pushed from the child iframe
   */
  onStatusPush(status: StatusMessageInterface): void {
    this.lastPushedStatus = status;
    this.statusPushSubject$.next(status);
  }

  /**
   * Get the last status pushed from the iframe (real-time)
   * @returns The last StatusMessageInterface or null
   */
  getLastPushedStatus(): StatusMessageInterface | null {
    return this.lastPushedStatus;
  }

  /**
   * Observable stream of status push events
   * Use this for real-time reactive updates
   * @returns Observable of status changes
   */
  getStatusPushStream(): Subject<StatusMessageInterface> {
    return this.statusPushSubject$;
  }

  constructor(
    public modalService: ModalService,
  ) {
    this.setupCommands();
  }

  private setupCommands() {

    this.selectCommand = CommandFactory.createResultUICommand(
      async (returnedJsonIdentity: string) => {
        return returnedJsonIdentity;
      },
      () => of(true)
    );
    this.selectCommand.closeModal = true;
    this.selectCommand.isDefault = true;
    this.selectCommand.isVisible$ = new BehaviorSubject<boolean>(false);
    this.modalCommands.push(this.selectCommand);
  }
}
